/**
 * Sign in with Apple JS のローダと signIn ラッパ（ADR 0036 / feast-be ADR 0074）。
 *
 * GIS（Google Identity Services）と同様に script タグを動的ロードする。
 * popup モードを使い、リダイレクトせずに id_token を受け取る。
 *
 * 注意:
 * - Apple は Return URL に localhost / http を登録できないため、ローカル開発では
 *   ボタン自体を出さない（isAppleLoginEnabled。実認可の確認は stg で行う）。
 */

const APPLE_SCRIPT_SRC =
  'https://appleid.cdn-apple.com/appleauth/static/jsapi/appleid/1/ja_JP/appleid.auth.js';
// Services ID は公開識別子（ビルド後の JS に必ず露出する値）のため既定値をコード側に持つ。
// 環境変数 REACT_APP_APPLE_CLIENT_ID があればそちらを優先（環境別の差し替え用）。
const APPLE_CLIENT_ID = process.env.REACT_APP_APPLE_CLIENT_ID || 'jp.co.feast.web';

export interface AppleSignInResult {
  idToken: string;
  /** 氏名。Apple は初回認可時にしか返さないため、取得できた場合のみ入る（姓 名）。 */
  name?: string;
}

/**
 * Apple ログインを有効にするか。
 * Apple は Return URL に localhost / http を登録できないため、
 * https で配信される環境（stg / prod）でのみボタンを表示する。
 */
export function isAppleLoginEnabled(): boolean {
  return (
    Boolean(APPLE_CLIENT_ID) &&
    window.location.protocol === 'https:' &&
    !['localhost', '127.0.0.1'].includes(window.location.hostname)
  );
}

let loadingPromise: Promise<void> | null = null;

function loadAppleScript(): Promise<void> {
  if (window.AppleID) return Promise.resolve();
  if (loadingPromise) return loadingPromise;
  loadingPromise = new Promise((resolve, reject) => {
    const script = document.createElement('script');
    script.src = APPLE_SCRIPT_SRC;
    script.onload = () => resolve();
    script.onerror = () => {
      loadingPromise = null;
      reject(new Error('Apple 認証スクリプトの読み込みに失敗しました'));
    };
    document.body.appendChild(script);
  });
  return loadingPromise;
}

/**
 * Apple のサインインポップアップを開き、id_token（と初回のみ氏名）を返す。
 * ユーザーがポップアップを閉じた場合は reject される（isAppleSignInCancel で判別）。
 */
export async function signInWithApple(): Promise<AppleSignInResult> {
  if (!APPLE_CLIENT_ID) {
    throw new Error('REACT_APP_APPLE_CLIENT_ID が設定されていません');
  }
  await loadAppleScript();
  // init は冪等（最後の設定が有効）。redirectURI は popup モードでも
  // Apple Developer Portal に登録済みの Return URL と一致している必要がある。
  window.AppleID.auth.init({
    clientId: APPLE_CLIENT_ID,
    scope: 'name email',
    redirectURI: `${window.location.origin}/login`,
    usePopup: true,
  });
  const response = await window.AppleID.auth.signIn();
  const idToken: string | undefined = response?.authorization?.id_token;
  if (!idToken) {
    throw new Error('Apple サインインに失敗しました');
  }
  // user（氏名）は初回認可レスポンスにのみ含まれる。日本語圏を主対象とするため「姓 名」順。
  const firstName: string = response?.user?.name?.firstName ?? '';
  const lastName: string = response?.user?.name?.lastName ?? '';
  const name = `${lastName} ${firstName}`.trim();
  return { idToken, name: name || undefined };
}

/** signIn の reject がユーザーによるキャンセル（ポップアップを閉じた）かどうか。 */
export function isAppleSignInCancel(error: unknown): boolean {
  const code =
    typeof error === 'object' && error !== null && 'error' in error
      ? String((error as { error: unknown }).error)
      : '';
  return code === 'popup_closed_by_user' || code === 'user_cancelled_authorize';
}
